import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isAuthenticated: boolean;
  noWorkspaceAccess: boolean; // True if user is authenticated but has no workspace access
  joinChat: (chatId: string) => void;
  leaveChat: (chatId: string) => void;
  sendTyping: (chatId: string, isTyping: boolean) => void;
  connectNow: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
  apiBaseUrl: string;
  autoConnect?: boolean;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children, apiBaseUrl, autoConnect = true }) => {
  const { idToken, currentUser } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [noWorkspaceAccess, setNoWorkspaceAccess] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const currentChatRef = useRef<string | null>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTokenRef = useRef<string | null>(null);
  const connectIntentRef = useRef<boolean>(autoConnect !== false);

  // Extract WebSocket URL from apiBaseUrl
  const getSocketUrl = useCallback((baseUrl: string) => {
    // Convert http://localhost:8080/api to http://localhost:8080
    // Convert https://example.com/api to https://example.com
    try {
      const url = new URL(baseUrl);
      return `${url.protocol}//${url.host}`;
    } catch (e) {
      console.error('Failed to parse apiBaseUrl for WebSocket:', e);
      return baseUrl.replace(/\/api\/?$/, '');
    }
  }, []);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    setSocket(null);
    setIsConnected(false);
    setIsAuthenticated(false);
    setNoWorkspaceAccess(false);
    lastTokenRef.current = null;
  }, []);

  // Function to refresh token and reauthenticate with WebSocket
  const refreshAndReauthenticate = useCallback(async (socket: Socket) => {
    try {
      if (!currentUser) {
        console.error('WebSocket: No current user for token refresh');
        return false;
      }

      console.log('WebSocket: Forcing token refresh for reauthentication');
      const freshToken = await currentUser.getIdToken(true); // Force refresh
      
      if (!freshToken) {
        console.error('WebSocket: Failed to get fresh token');
        return false;
      }
      
      lastTokenRef.current = freshToken;

      if (socket.connected) {
        console.log('WebSocket: Reauthenticating with fresh token');
        socket.emit('authenticate', { token: freshToken });
        return true;
      } else {
        console.warn('WebSocket: Socket not connected, cannot reauthenticate');
        return false;
      }
    } catch (error) {
      console.error('WebSocket: Failed to refresh token for reauthentication:', error);
      // User might need to log in again
      return false;
    }
  }, [currentUser]);

  // Function to setup automatic token refresh
  const setupTokenRefresh = useCallback((socket: Socket) => {
    // Clear any existing interval
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
    }

    // Refresh token every 50 minutes (before 60-minute expiration)
    tokenRefreshIntervalRef.current = setInterval(async () => {
      try {
        if (!currentUser) {
          console.warn('WebSocket: No current user for periodic token refresh');
          return;
        }

        if (!socket.connected) {
          console.warn('WebSocket: Socket not connected, skipping token refresh');
          return;
        }

        console.log('WebSocket: Performing scheduled token refresh');
        const freshToken = await currentUser.getIdToken(true); // Force refresh
        
        // Only send if token actually changed
        if (freshToken !== lastTokenRef.current) {
          lastTokenRef.current = freshToken;
          
          console.log('WebSocket: Sending token refresh to server');
          socket.emit('token_refresh', { token: freshToken });
        } else {
          console.log('WebSocket: Token unchanged, skipping refresh message');
        }
      } catch (error) {
        console.error('WebSocket: Scheduled token refresh failed:', error);
        // Don't disconnect, just log the error
      }
    }, 50 * 60 * 1000); // 50 minutes

    console.log('WebSocket: Token refresh interval setup (every 50 minutes)');
  }, [currentUser]);

  const connectSocket = useCallback(() => {
    if (!idToken) {
      console.log('WebSocket: No idToken available, skipping connection');
      cleanup();
      return;
    }

    if (socketRef.current?.connected) {
      console.log('WebSocket: Already connected');
      return;
    }

    cleanup();

    const socketUrl = getSocketUrl(apiBaseUrl);
    console.log('WebSocket: Connecting to:', socketUrl);

    // Store current token
    lastTokenRef.current = idToken;

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: maxReconnectAttempts,
      timeout: 30000,  // Increased to 30s to handle large datasets
      autoConnect: true,
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('WebSocket: Connected with ID:', newSocket.id);
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      // Authenticate immediately with cached token (no async operations)
      // This prevents connection timeouts for clients with large datasets
      const tokenToUse = lastTokenRef.current;
      
      if (!tokenToUse) {
        console.error('WebSocket: No token available for authentication');
        // Try async refresh as fallback
        refreshAndReauthenticate(newSocket);
        return;
      }

      // Authenticate with Firebase token - synchronously
      console.log('WebSocket: Sending authentication with cached token');
      newSocket.emit('authenticate', { token: tokenToUse });
    });

    newSocket.on('authenticated', (data) => {
      console.log('WebSocket: Authenticated successfully', data);
      setIsAuthenticated(true);
      
      // Check if user has no workspace access
      if (data.noWorkspaceAccess) {
        console.log('WebSocket: User authenticated but has no workspace access');
        setNoWorkspaceAccess(true);
        // Don't try to rejoin chat or setup refresh for users without workspace access
        return;
      }
      
      setNoWorkspaceAccess(false);

      // Rejoin current chat if there was one
      if (currentChatRef.current) {
        console.log('WebSocket: Rejoining chat after reconnection:', currentChatRef.current);
        newSocket.emit('join_chat', { chatId: currentChatRef.current });
      }

      // Setup token refresh interval after successful authentication
      setupTokenRefresh(newSocket);
    });

    // Add event listener for joined_chat confirmation
    newSocket.on('joined_chat', (data) => {
      console.log('✅ WebSocket: Successfully joined chat room:', data);
    });

    // Add event listener for left_chat confirmation
    newSocket.on('left_chat', (data) => {
      console.log('✅ WebSocket: Successfully left chat room:', data);
    });

    // Add error handler for room operations
    newSocket.on('error', (data) => {
      console.error('❌ WebSocket error:', data);
    });

    newSocket.on('auth_error', (data) => {
      console.error('WebSocket: Authentication error:', data);
      setIsAuthenticated(false);
      
      // Handle various authentication errors
      const errorMessage = data.message?.toLowerCase() || '';
      const shouldRetry = 
        errorMessage.includes('expired') || 
        errorMessage.includes('invalid') ||
        errorMessage.includes('token');
      
      if (shouldRetry) {
        console.log('WebSocket: Token issue detected, attempting to refresh and reauthenticate');
        refreshAndReauthenticate(newSocket);
      } else {
        console.error('WebSocket: Non-recoverable authentication error, cleaning up');
        // For other errors, might need user to re-login
        cleanup();
      }
    });

    newSocket.on('auth_refresh_success', (data) => {
      console.log('WebSocket: Token refresh confirmed by server', data);
    });

    newSocket.on('auth_refresh_error', (data) => {
      console.error('WebSocket: Token refresh failed on server', data);
      // Attempt to get a fresh token and reauthenticate
      refreshAndReauthenticate(newSocket);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('WebSocket: Disconnected:', reason);
      setIsConnected(false);
      setIsAuthenticated(false);

      // Handle reconnection for certain disconnect reasons
      if (reason === 'io server disconnect') {
        // Server disconnected us, manually reconnect
        reconnectAttemptsRef.current++;
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`WebSocket: Attempting reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
          reconnectTimeoutRef.current = setTimeout(() => {
            newSocket.connect();
          }, delay);
        } else {
          console.error('WebSocket: Max reconnection attempts reached');
        }
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket: Connection error:', error);
      setIsConnected(false);
      setIsAuthenticated(false);
    });

    newSocket.on('error', (data) => {
      console.error('WebSocket: Error event:', data);
    });

    socketRef.current = newSocket;
    setSocket(newSocket);
  }, [idToken, apiBaseUrl, cleanup, getSocketUrl, setupTokenRefresh, refreshAndReauthenticate]);

  const requestConnection = useCallback(() => {
    if (!connectIntentRef.current) {
      connectIntentRef.current = true;
    }

    if (!idToken) {
      console.log('WebSocket: Cannot connect yet, no idToken available');
      return;
    }

    connectSocket();
  }, [idToken, connectSocket]);

  // Initialize WebSocket connection when idToken is available
  // Add a small delay to prevent connection timeouts during initial page load
  useEffect(() => {
    if (autoConnect !== false) {
      connectIntentRef.current = true;
      if (idToken && !socketRef.current) {
        connectSocket();
      }
    }
  }, [autoConnect, idToken, connectSocket]);

  useEffect(() => {
    if (!connectIntentRef.current) {
      if (!idToken) {
        cleanup();
      }
      return;
    }

    if (!idToken) {
      cleanup();
      return;
    }

    const connectionTimer = setTimeout(() => {
      connectSocket();
    }, 500);

    return () => {
      clearTimeout(connectionTimer);
      cleanup();
    };
  }, [idToken, connectSocket, cleanup]);

  // Handle token changes from AuthContext (when token is refreshed)
  useEffect(() => {
    // If we have a connected socket and the token changed (but is still valid)
    if (socketRef.current?.connected && idToken && lastTokenRef.current !== idToken) {
      console.log('WebSocket: Token changed in AuthContext, sending refresh to server');
      lastTokenRef.current = idToken;
      socketRef.current.emit('token_refresh', { token: idToken });
    }
  }, [idToken]);

  // Join a specific chat room
  const joinChat = useCallback((chatId: string) => {
    if (socketRef.current?.connected && isAuthenticated) {
      console.log('🚀 WebSocket: Emitting join_chat for:', chatId);
      socketRef.current.emit('join_chat', { chatId });
      currentChatRef.current = chatId;
    } else {
      console.warn('⚠️ WebSocket: Cannot join chat, not connected or authenticated', {
        hasSocket: !!socketRef.current,
        connected: socketRef.current?.connected,
        isAuthenticated
      });
    }
  }, [isAuthenticated]);

  // Leave a specific chat room
  const leaveChat = useCallback((chatId: string) => {
    if (socketRef.current?.connected) {
      console.log('WebSocket: Leaving chat:', chatId);
      socketRef.current.emit('leave_chat', { chatId });
      if (currentChatRef.current === chatId) {
        currentChatRef.current = null;
      }
    }
  }, []);

  // Send typing indicator
  const sendTyping = useCallback((chatId: string, isTyping: boolean) => {
    if (socketRef.current?.connected && isAuthenticated) {
      socketRef.current.emit('typing', { chatId, isTyping });
    }
  }, [isAuthenticated]);

  const value: WebSocketContextType = {
    socket,
    isConnected,
    isAuthenticated,
    noWorkspaceAccess,
    joinChat,
    leaveChat,
    sendTyping,
    connectNow: requestConnection,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
